import React, { Fragment, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import AuditForm from '../../components/AuditForm';
import icon from '../../images/icon/magic.svg';
import Seo from '../../components/Seo';

const AuditPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [displayMonth, setDisplayMonth] = useState(new Date().getMonth());
    const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

    useEffect(() => {
        if (lang && (lang === 'en' || lang === 'ka')) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    const now = new Date();
    const today = (displayMonth === now.getMonth() && displayYear === now.getFullYear()) ? now.getDate() : -1;
    const currentMonth = displayMonth;
    const currentYear = displayYear;

    const prevMonth = () => {
        if (currentMonth === 0) {
            setDisplayMonth(11);
            setDisplayYear(currentYear - 1);
        } else {
            setDisplayMonth(currentMonth - 1);
        }
        setSelectedDay(null);
        setSelectedTime(null);
    };

    const nextMonth = () => {
        if (currentMonth === 11) {
            setDisplayMonth(0);
            setDisplayYear(currentYear + 1);
        } else {
            setDisplayMonth(currentMonth + 1);
        }
        setSelectedDay(null);
        setSelectedTime(null);
    };
    const isWeekday = (day) => {
        const d = new Date(currentYear, currentMonth, day).getDay();
        return d !== 0 && d !== 6;
    };
    const monthNames = {
        en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        ka: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი']
    };
    const currentLang = i18n.language || 'en';
    const monthName = `${monthNames[currentLang][currentMonth]} ${currentYear}`;
    const dayNames = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const timeSlots = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

    return (
        <Fragment>
            <Seo
                title="Free Web Audit | OrbitroniX"
                description="Get a free technical audit for your website."
                keywords="web audit, website analysis, free audit, technical SEO"
                url={lang ? `/${lang}/audit` : '/audit'}
            />
            <Layout>
                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="page-title pt-150 pos-rel bg_img"
                >
                    <div className="container">
                        <div className="page-title-wrap sd-title-wrap">
                            <div className="row mt-none-30 align-items-end">
                                <div className="col-lg-9 mt-30">
                                    <motion.div
                                        initial={{ opacity: 0, x: -50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="page-title-box"
                                    >
                                        <motion.span
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.5 }}
                                            className="sub-title"
                                        >
                                            <img src={icon} alt="" /> {t('audit.freeAudit', 'Free Audit')}
                                        </motion.span>
                                        <motion.h2
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.7 }}
                                            className="title"
                                        >
                                            {t('audit.title')}
                                        </motion.h2>
                                    </motion.div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <motion.section
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="contact pt-85 pb-120"
                >
                    <div className="container">
                        <div className="row mt-none-30">
                            <div className="col-lg-8">
                                <div className="cs-contact-wrap cs-contact-form cd-contact-form item-contact_form">
                                    <h3 className="xb-item--title">{t('audit.formTitle')}</h3>
                                    <AuditForm selectedDay={selectedDay} selectedTime={selectedTime} />
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="item-contact_info">
                                    <div className="orbx-item--inner">
                                        <div className="orbx-item--top">
                                            <h3 className="orbx-item--title">{t('audit.availability')}</h3>
                                            <div className="audit-calendar">
                                                <div className="cal-header">
                                                    <button className="cal-nav" onClick={prevMonth}><i className="fas fa-chevron-left"></i></button>
                                                    <span className="cal-month">{monthName}</span>
                                                    <button className="cal-nav" onClick={nextMonth}><i className="fas fa-chevron-right"></i></button>
                                                </div>
                                                <div className="cal-grid">
                                                    {dayNames.map(d => (
                                                        <div key={d} className="cal-day-name">{t(`audit.calendar.${d}`)}</div>
                                                    ))}
                                                    {[...Array(new Date(currentYear, currentMonth, 1).getDay() === 0 ? 6 : new Date(currentYear, currentMonth, 1).getDay() - 1)].map((_, i) => (
                                                        <div key={`empty-${i}`} className="cal-day"></div>
                                                    ))}
                                                    {[...Array(new Date(currentYear, currentMonth + 1, 0).getDate())].map((_, i) => {
                                                        const day = i + 1;
                                                        const isAvail = isWeekday(day);
                                                        const isToday = day === today;
                                                        const isSelected = day === selectedDay;
                                                        return (
                                                            <div
                                                                key={day}
                                                                className={`cal-day ${isAvail ? 'available' : 'unavailable'} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                                                                onClick={() => isAvail && setSelectedDay(day)}
                                                                style={{ cursor: isAvail ? 'pointer' : 'not-allowed' }}
                                                            >
                                                                {day}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="cal-legend">
                                                    <span className="cal-legend-item"><span className="cal-dot available"></span> {t('audit.selectDay')}</span>
                                                    <span className="cal-legend-item"><span className="cal-dot selected"></span> {t('audit.selectedDay')}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {selectedDay && (
                                            <div className="audit-time-picker mt-4">
                                                <h4 className="text-white mb-3" style={{ fontSize: '14px' }}>{t('audit.selectTime')}</h4>
                                                <div className="time-grid">
                                                    {timeSlots.map(time => (
                                                        <button
                                                            key={time}
                                                            className={`time-slot ${selectedTime === time ? 'active' : ''}`}
                                                            onClick={() => setSelectedTime(time)}
                                                        >
                                                            {time}
                                                        </button>
                                                    ))}
                                                </div>
                                                {selectedTime && (
                                                    <div className="selected-summary mt-3">
                                                        <span className="text-white-50" style={{ fontSize: '13px' }}>
                                                            {t('audit.selectedDay')}: <strong className="text-white">{selectedDay}</strong> | {t('audit.selectedTime')}: <strong className="text-white">{selectedTime}</strong>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <style>{`
                    .audit-calendar {
                        background: rgb(8 11 37);
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        border-radius: 16px;
                        padding: 20px;
                        margin-top: 15px;
                    }
                    .cal-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: 15px;
                    }
                    .cal-nav {
                        background: rgba(255,255,255,0.05);
                        border: 1px solid rgba(255,255,255,0.1);
                        color: #fff;
                        width: 28px;
                        height: 28px;
                        border-radius: 6px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 12px;
                        transition: all 0.2s;
                    }
                    .cal-nav:hover {
                        background: rgba(108, 92, 231, 0.3);
                        border-color: #6c5ce7;
                    }
                    .cal-month {
                        color: #fff;
                        font-weight: 600;
                        font-size: 16px;
                    }
                    .cal-grid {
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        gap: 4px;
                    }
                    .cal-day-name {
                        text-align: center;
                        color: rgba(255, 255, 255, 0.4);
                        font-size: 11px;
                        font-weight: 600;
                        padding: 4px 0;
                        text-transform: uppercase;
                    }
                    .cal-day {
                        text-align: center;
                        padding: 6px 2px;
                        border-radius: 6px;
                        font-size: 12px;
                        color: rgba(255, 255, 255, 0.5);
                        transition: all 0.2s ease;
                    }
                    .cal-day.available:hover {
                        background: rgba(46, 204, 113, 0.3);
                        transform: scale(1.1);
                    }
                    .cal-day.available {
                        background: rgba(46, 204, 113, 0.15);
                        color: #2ecc71;
                    }
                    .cal-day.unavailable {
                        background: rgba(255, 71, 87, 0.25);
                        color: #ff4757;
                        text-decoration: line-through;
                        cursor: not-allowed;
                    }
                    .cal-day.today {
                        outline: 2px solid #6c5ce7;
                        font-weight: 700;
                    }
                    .cal-day.selected {
                        background: #6c5ce7 !important;
                        color: #fff !important;
                        font-weight: 700;
                    }
                    .cal-legend {
                        display: flex;
                        gap: 15px;
                        margin-top: 12px;
                        padding-top: 12px;
                        border-top: 1px solid rgba(255, 255, 255, 0.05);
                    }
                    .cal-legend-item {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-size: 12px;
                        color: rgba(255, 255, 255, 0.5);
                    }
                    .cal-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        display: inline-block;
                    }
                    .cal-dot.available {
                        background: #2ecc71;
                    }
                    .cal-dot.selected {
                        background: #6c5ce7;
                    }
                    .time-grid {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 8px;
                    }
                    .time-slot {
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 8px;
                        padding: 8px 4px;
                        color: rgba(255, 255, 255, 0.6);
                        font-size: 13px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                    }
                    .time-slot:hover {
                        background: rgba(108, 92, 231, 0.2);
                        border-color: rgba(108, 92, 231, 0.4);
                        color: #fff;
                    }
                    .time-slot.active {
                        background: #6c5ce7;
                        border-color: #6c5ce7;
                        color: #fff;
                        font-weight: 600;
                    }
                    .selected-summary {
                        padding: 10px;
                        background: rgba(108, 92, 231, 0.1);
                        border-radius: 8px;
                        border: 1px solid rgba(108, 92, 231, 0.2);
                        text-align: center;
                    }
                `}</style>
            </Layout>
        </Fragment>
    );
};

export default AuditPage;